/**
 * Pure helpers for the ADHD Manage Documents dashboard chrome.
 *
 * Kept outside the React component so unit tests can lock the TOC / SLaM
 * Report Builder rules (no Cover Page row, Download Sections copy, processing
 * keys) without mounting the full dashboard.
 */

/**
 * Firestore processing-status keys that should disable the CAR / sections
 * download button while a generation job is in flight.
 *
 * When the workflow omits the standalone Cover Page (TOC + SLaM Report
 * Builder), Cover Page is excluded so a stale or in-flight `cpReport*` job
 * cannot disable Download after the Cover Page row has been removed from the UI.
 *
 * @param {boolean} replaceCARwithReportBuilder
 * @returns {string[]}
 */
export function getCarSectionProcessingKeys(replaceCARwithReportBuilder) {
  const keys = [
    'clinicalInterview',
    'semiStructuredInterview',
    'diagnosticDecision',
    'postAssessmentDiscussion',
    'qbCheck',
    'qbTest',
    'ratingScales',
  ];
  if (!replaceCARwithReportBuilder) {
    keys.push('coverPage');
  }
  return keys;
}

/**
 * Panel title, download button visibility/label, and bundle dialog title for
 * the main assessment documents panel.
 *
 * TOC and SLaM share the Report Builder shell (no Cover Page). Only TOC hides
 * the top-level Download button — SLaM keeps it, renamed to "Download Sections".
 *
 * @param {{ replaceCARwithReportBuilder: boolean, isTOCWorkflow: boolean }} opts
 * @returns {{
 *   panelTitle: string,
 *   showDownloadButton: boolean,
 *   downloadButtonLabel: string,
 *   bundleDialogTitle: string,
 * }}
 */
export function getManageDocumentsPanelCopy({ replaceCARwithReportBuilder, isTOCWorkflow }) {
  return {
    panelTitle: replaceCARwithReportBuilder
      ? 'Report Builder'
      : 'Clinical Assessment Report',
    showDownloadButton: !isTOCWorkflow,
    downloadButtonLabel: replaceCARwithReportBuilder
      ? 'Download Sections'
      : 'Download Report',
    bundleDialogTitle: replaceCARwithReportBuilder
      ? 'Download Sections'
      : 'Download Clinical Assessment Report',
  };
}

/**
 * Whether the Manage Documents section list should include a Cover Page row.
 * Inverse of replaceCARwithReportBuilder — named for call-site readability.
 *
 * @param {boolean} replaceCARwithReportBuilder
 * @returns {boolean}
 */
export function shouldIncludeCoverPageSection(replaceCARwithReportBuilder) {
  return !replaceCARwithReportBuilder;
}

/**
 * Circle numbers for the Report Builder / Clinical Assessment Report list.
 *
 * A row that the diagnosis path hides (SSI, DD and PAD after an early exit,
 * or SSI and PAD on a standard not-ADHD assessment) must not keep a number,
 * or the rows still on screen would skip ahead. Numbers run 1, 2, 3 in the
 * order the clinician sees them.
 *
 * The previous version patched two specific gaps by hand, and both patches
 * assumed a Cover Page was row 1 and the Diagnostic Decision row was still
 * visible. An early-exit SLaM report has neither, so Rating Scales stayed
 * on 4. Counting only the visible rows covers that case and the older ones
 * with the same rule.
 *
 * @param {Array<{ id: string }>} sections - Full list, in display order.
 * @param {(sectionId: string) => boolean} isHidden - True when the row is
 *   omitted (see isSectionHiddenByDiagnosisPath).
 * @returns {Map<string, number>} Visible section id to its circle number.
 */
export function getVisibleSectionDisplayNumbers(sections, isHidden) {
  const numbers = new Map();
  let next = 1;
  for (const section of sections || []) {
    if (!section?.id || isHidden(section.id)) continue;
    numbers.set(section.id, next);
    next += 1;
  }
  return numbers;
}

/**
 * Ordered core section IDs for the Clinical Assessment Report / Report Builder
 * panel (before optional QbCheck / QbTest rows).
 *
 * Phase 1 SLaM and standard orgs include `coverPage` first. Phase 2 SLaM and TOC
 * (replaceCARwithReportBuilder) omit it — clinicians approve CI / SSI / DD / PAD
 * (and attachments) as individual sections instead.
 *
 * Mirrors the conditional spread in DocumentsDashboard.getDocumentSections.
 *
 * @param {boolean} replaceCARwithReportBuilder
 * @returns {string[]}
 */
export function buildCoreCarSectionIds(replaceCARwithReportBuilder) {
  const core = [
    'clinicalInterview',
    'semiStructuredInterview',
    'diagnosticDecision',
    'postAssessmentDiscussion',
    'attachmentsRatingScales',
  ];
  if (shouldIncludeCoverPageSection(replaceCARwithReportBuilder)) {
    return ['coverPage', ...core];
  }
  return core;
}

/**
 * Whether a section id may appear in the bundle download picker for this workflow.
 * Cover Page is excluded when replaceCARwithReportBuilder is true (Report Builder).
 *
 * @param {string} sectionId
 * @param {boolean} replaceCARwithReportBuilder
 * @returns {boolean}
 */
export function isSectionIncludedInBundleDialog(sectionId, replaceCARwithReportBuilder) {
  if (sectionId === 'coverPage') {
    return shouldIncludeCoverPageSection(replaceCARwithReportBuilder);
  }
  return true;
}

/**
 * Filter a Manage Documents section list for ReportBundleDialog.visibleSections.
 * Applies the Cover Page workflow gate; caller still applies diagnosis-path and
 * Qb visibility filters.
 *
 * @param {Array<{ id: string }>} sections
 * @param {boolean} replaceCARwithReportBuilder
 * @returns {Array<{ id: string }>}
 */
export function filterSectionsForBundleDialog(sections, replaceCARwithReportBuilder) {
  return sections.filter((section) =>
    isSectionIncludedInBundleDialog(section.id, replaceCARwithReportBuilder),
  );
}

/**
 * Title for the lower panel (Patient Report, BUPA Letter, Semble MDT, etc.).
 * TOC and SLaM Phase 2 (replaceCARwithReportBuilder) label it "Final Assessment
 * Report"; standard orgs keep "Additional Documents".
 *
 * @param {boolean} replaceCARwithReportBuilder
 * @returns {string}
 */
export function getAdditionalDocumentsPanelTitle(replaceCARwithReportBuilder) {
  return replaceCARwithReportBuilder
    ? 'Final Assessment Report'
    : 'Additional Documents';
}

/**
 * Human-readable reason shown in the bundle dialog when an approved section
 * cannot be included in a PDF download.
 *
 * Maps the three "no PDF" states from `getPdfAvailability` to clinician copy:
 *   - 'pending' -> the background retry is still working on it
 *   - 'error'   -> the PDF failed for good (the row offers Try again)
 *   - anything else (no entry, DOCX only) -> generic not-available
 *
 * @param {'ready'|'pending'|'error'|'missing'} availability
 * @returns {string}
 */
export function getPdfExclusionReason(availability) {
  if (availability === 'pending') return 'PDF is being prepared';
  if (availability === 'error') return 'PDF could not be created';
  return 'PDF not available';
}

/**
 * Build the list of approved sections the PDF bundle will silently miss and
 * why, so the dialog can tell the clinician instead of dropping them.
 *
 * Only the PDF format is affected: a DOCX bundle reads `currentPath`, which
 * exists whenever the DOCX stage succeeded. Sections that are not approved
 * are not "excluded" (they were never eligible) and are left out of the list.
 *
 * @param {Object} params
 * @param {'pdf'|'docx'} params.selectedFormat
 * @param {Array<{ id: string, reportStage: string, status: string }>} params.visibleSections
 * @param {Object<string, boolean>} params.availabilityMap - sectionId -> has a file for the chosen format
 * @param {Object} params.documents - `patientDataModel.documents`
 * @param {Object<string, string>} params.sectionKeyMapping - sectionId -> documents key
 * @param {(entry: Object) => string} params.getAvailability - normally `getPdfAvailability`
 * @returns {Array<{ id: string, name: string, reason: string }>}
 */
/**
 * Detects a Patient Report that was generated during an early-exit phase
 * (assessment ended after CI) and is now stale because the assessment was
 * subsequently resumed.  The report is still downloadable/previewable but
 * the clinician needs a visual cue that it only covers the Clinical Interview.
 *
 * Returns true when ALL of the following hold:
 *   1. The assessment was resumed (`assessmentEnd.resumedAt` is truthy).
 *   2. The assessment is NOT currently ended (`wf.assessmentEndedAfterCI` is false).
 *   3. A Patient Report exists from the early-exit phase (status is 'approved'
 *      or 'ready-for-review').
 *   4. The full assessment is not yet complete — the remaining required sections
 *      (SSI, DD, PAD for ADHD) are not all approved.
 *
 * Self-clearing: once all required sections are approved, condition 4 fails
 * and the v2 auto-gen pipeline (`rerenderPatientReportIfStale`) regenerates
 * the PR.  Re-ending is safe because `buildEndAssessmentUpdates` writes a
 * whole-object `assessmentEnd` with `resumedAt: null`, clearing condition 1.
 *
 * @param {{ id: string, status: string, areAllReportsApproved: boolean }} doc
 *   The Patient Report entry from the additionalDocs array.
 * @param {{ resumedAt?: any, endedAt?: any, endedByName?: string }|null|undefined} assessmentEnd
 *   The `patientDataModel.assessmentEnd` object.
 * @param {{ assessmentEndedAfterCI?: boolean }} wf
 *   The resolved workflow config for the patient.
 * @returns {boolean}
 */
export function isStaleEarlyExitPatientReport(doc, assessmentEnd, wf) {
  if (doc?.id !== 'patientReport') return false;
  if (!assessmentEnd?.resumedAt) return false;
  if (wf?.assessmentEndedAfterCI) return false;
  if (doc.status !== 'approved' && doc.status !== 'ready-for-review') return false;
  if (doc.areAllReportsApproved) return false;
  return true;
}

export function buildExcludedSections({
  selectedFormat,
  visibleSections = [],
  availabilityMap = {},
  documents = {},
  sectionKeyMapping = {},
  getAvailability,
}) {
  if (selectedFormat !== 'pdf') return [];
  return visibleSections
    .filter((s) => s.status === 'approved' && !availabilityMap[s.id])
    .map((s) => {
      const entry = documents[sectionKeyMapping[s.id]];
      return {
        id: s.id,
        name: s.reportStage,
        reason: getPdfExclusionReason(getAvailability(entry)),
      };
    });
}
